/**
 * The glyph before a human visitor's name (founder 2026-10-13): the
 * country flag when the edge resolved a country, else the person glyph.
 * Same flag source as the detail panel's Location row (`CountryFlag`,
 * flagcdn.com); no new third party. `ZZ` (unknown) and malformed codes
 * fall back to the person glyph.
 */

import { User } from 'lucide-react';

import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { useC } from '../../lib/theme.js';

/** A two-letter, non-`ZZ` country code, upper-cased; else null. */
export function flagCountry(code: string | null | undefined): string | null {
  if (typeof code !== 'string' || !/^[A-Za-z]{2}$/.test(code)) return null;
  const upper = code.toUpperCase();
  return upper === 'ZZ' ? null : upper;
}

export function PersonGlyph({
  country,
  size = 14,
  color,
}: {
  readonly country: string | null | undefined;
  /** Glyph box: 14 / 16 → 16×12 flag, 20 → 20×15 flag. */
  readonly size?: number;
  readonly color?: string;
}): JSX.Element {
  const C = useC();
  const code = flagCountry(country);
  if (code === null) {
    return <User size={size} color={color ?? C.muted} style={{ flexShrink: 0 }} aria-label="Person" data-testid="person-glyph" />;
  }
  return (
    <span className="flex-shrink-0 inline-flex items-center" data-testid="person-flag" data-country={code}>
      <CountryFlag code={code} size={size >= 18 ? 20 : 16} />
    </span>
  );
}
